"use client";
import { useMemo } from "react";
import { useSelector } from "react-redux";

import {
  getIsBundleShop,
  getIsLockerShop,
  getIsPricelessShop,
  getStoreCurrencyCode,
  getStorePriceShouldRoundOff,
} from "@selectors/store";

import { isByoProduct } from "@helpers/products/types";
import { isCustomCurrency } from "@stadium/helpers/src/currency";
import { defaultAscendingSort } from "@stadium/helpers/array-methods";

import WeglotNoTranslationWrapper from "@stadium/services/weglot/no-translation-wrapper";

import useStoreCurrencyConverter from "@hooks/currency/use-store-currency-converter";
import { useStoreContext } from "@components/store/provider";

import { US_CURRENCY_CODE } from "@constants/store";

import styles from "./product-tile.module.scss";

const emptyObj = {};

export const ProductPrice = ({ product = emptyObj, source, productPriceClass, intent }) => {
  const isLockerShop = useSelector(getIsLockerShop);
  const isBundleShop = useSelector(getIsBundleShop);
  const storeCurrencyCode = useSelector(getStoreCurrencyCode);
  const isPricelessShop = useSelector(getIsPricelessShop);
  const priceShouldRoundOff = useSelector(getStorePriceShouldRoundOff);

  const { storeDisplayCurrencyCode } = useStoreContext();
  const { formatPriceInDisplayStoreCurrency } = useStoreCurrencyConverter();

  const {
    usdPrices: prices = [],
    bulkAvailable: isBulkAvailable,
    hasTierPricing,
    priceIn,
    amountIn,
  } = product;

  const priceInUSD = source === "search";

  const priceFormatOptions = useMemo(
    () => ({
      ceilRequiredBeforeConversion: !isCustomCurrency(storeCurrencyCode) && priceShouldRoundOff,
      ceilRequired: isCustomCurrency(storeCurrencyCode) && priceShouldRoundOff,
    }),
    [storeCurrencyCode, priceShouldRoundOff],
  );

  const displayPriceCurrencyCode = useMemo(() => {
    return priceInUSD ? US_CURRENCY_CODE : storeCurrencyCode;
  }, [priceInUSD, storeCurrencyCode]);

  const displayPrice = useMemo(() => {
    const sortedPrices = [...(prices || [])].sort((a, b) => a - b);

    const nonBulkDisplayPrice = sortedPrices[0] || priceIn;

    if (isPricelessShop) {
      return null;
    } else if (prices?.length > 1) {
      if (isBulkAvailable || isLockerShop || isBundleShop || hasTierPricing) {
        const pricesToDisplay = [...prices].sort(defaultAscendingSort);
        return (
          <>
            <span className={styles.asLowAs}>As low as </span>
            <WeglotNoTranslationWrapper>
              {formatPriceInDisplayStoreCurrency(
                pricesToDisplay[0],
                displayPriceCurrencyCode,
                priceFormatOptions,
              )}
            </WeglotNoTranslationWrapper>
          </>
        );
      }
      return (
        <WeglotNoTranslationWrapper>
          {`${formatPriceInDisplayStoreCurrency(
            sortedPrices[0],
            displayPriceCurrencyCode,
            priceFormatOptions,
          )} - ${formatPriceInDisplayStoreCurrency(
            sortedPrices[sortedPrices.length - 1],
            displayPriceCurrencyCode,
            priceFormatOptions,
          )}
              `}
        </WeglotNoTranslationWrapper>
      );
    } else if (nonBulkDisplayPrice) {
      return (
        <WeglotNoTranslationWrapper>
          {formatPriceInDisplayStoreCurrency(
            nonBulkDisplayPrice,
            displayPriceCurrencyCode,
            priceFormatOptions,
          )}
        </WeglotNoTranslationWrapper>
      );
    } else if (amountIn) {
      return <WeglotNoTranslationWrapper>{amountIn}</WeglotNoTranslationWrapper>;
    }
  }, [
    isPricelessShop,
    amountIn,
    prices,
    priceInUSD,
    formatPriceInDisplayStoreCurrency,
    hasTierPricing,
    priceFormatOptions,
    displayPriceCurrencyCode,
    storeDisplayCurrencyCode,
  ]);

  if (displayPrice) {
    return (
      <span className={`t-cl-1 tile-price ${styles.tilePrice} ${productPriceClass}`}>
        {isByoProduct(intent) && "Minimum "}
        {displayPrice}
      </span>
    );
  }

  return "";
};

export default ProductPrice;
